import {useEffect, useRef} from 'react'

import cx from 'classnames'
import PropTypes from 'prop-types'

import AtomButton, {atomButtonDesigns, atomButtonShapes, atomButtonSizes} from '@s-ui/react-atom-button'
import {atomIconColors, atomIconSizes} from '@s-ui/react-atom-icon'
import {useSuiContext} from '@s-ui/react-context'
import PrimitiveTypography, {primitiveTypographyDesign} from '@s-ui/react-primitive-typography'
import {useMediaQuery, useMediaQueryBreakpoints} from '@adv-mt/mt-layout-render-media-query'
import SearchSave, {searchSaveTrackingOrigins, searchSaveTypes} from '@adv-mt/mt-search-save'
import IconBellCheckOutline from '@adv-mt/mt-svg-icons/lib/BellCheckOutline.js'
import IconBellRingOutline from '@adv-mt/mt-svg-icons/lib/BellRingOutline.js'
import {AiIcon} from '@adv-mt/ui/ai-icon'
import {ICON_SIZES} from '@adv-mt/ui/icon'

import NetAdCard, {CARD_DESIGN} from './components/adCard/index.js'
import NetAdCarousel from './components/adCarousel/index.js'
import ContactOptions from './components/ContactOptions.js'
import NetEndChatCard from './components/endChatCard/EndChatCard.js'
import FilterTagsCard from './components/FilterTagsCard.js'
import LoadingSkeleton from './components/loadingSkeleton/LoadingSkeleton.js'
import MarkdownContent from './components/MarkdownContent.js'
import MessageReview from './components/messageReview/MessageReview.js'
import NetRetryButton from './components/NetRetryButton.js'
import {BLOCK_TYPES, MESSAGE_ROLES} from './config.js'

const BASE_CLASS = 'mt-NetMessageList'

const NetMessageList = ({
  messages = [],
  chatId,
  isWide = false,
  onNewChat,
  onReviewSuccess,
  onShowResults,
  onAdClick,
  onSearchSaved,
  setIsVisibleLoginModal,
  onRetry,
  retrying,
  restoring,
  onFavoriteChange,
  onClickContact
}) => {
  const {i18n, domain} = useSuiContext()

  const config = domain.get('config')
  const tracking = config.get('tracking')
  const isMobile = config.get('isMobile')
  const {LIST_TYPES: {NET_ASSISTANT_AD_CARD} = {}, AUTHENTICATION_HIT_INFORMATIONS_2} = tracking || {}

  const isHigherThanMobileResolution = useMediaQuery(useMediaQueryBreakpoints.sm, {
    defaultMatches: !isMobile
  })

  const lastMessageRef = useRef(null)
  const isInitialScrollRef = useRef(true)

  const lastMessageIsLoading = messages[messages.length - 1]?.loading ?? false
  const lastMessageIndex = messages.length - 1

  useEffect(() => {
    if (isInitialScrollRef.current) {
      isInitialScrollRef.current = false
      lastMessageRef.current?.scrollIntoView({behavior: 'auto', block: 'end'})
    } else {
      lastMessageRef.current?.scrollIntoView({behavior: 'smooth', block: 'start'})
    }
  }, [messages.length, lastMessageIsLoading])

  const hasEndChat = messages.some(
    message => message.role === MESSAGE_ROLES.ASSISTANT && message.blocks?.some(block => block.type === BLOCK_TYPES.END)
  )

  const saveButtonIconProps = {
    'aria-hidden': true,
    role: 'presentation',
    size: atomIconSizes.medium,
    color: atomIconColors.currentColor
  }

  return (
    <div className={`${BASE_CLASS}-messages`}>
      {messages.map((message, index) => {
        if (message.role === MESSAGE_ROLES.USER) {
          return (
            <div
              key={index}
              ref={index === lastMessageIndex ? lastMessageRef : null}
              className={`${BASE_CLASS}-bubble ${BASE_CLASS}-bubble--user`}
            >
              <PrimitiveTypography design={primitiveTypographyDesign.BODY_1}>{message.text}</PrimitiveTypography>
            </div>
          )
        }

        const userChatStep = Math.floor(index / 2) + 1

        return (
          <div
            key={index}
            ref={index === lastMessageIndex ? lastMessageRef : null}
            className={`${BASE_CLASS}-bubble ${BASE_CLASS}-bubble--assistant ${
              message.loading ? `${BASE_CLASS}-bubble--loading` : ''
            }`}
          >
            {message.loading ? (
              <LoadingSkeleton />
            ) : (
              <>
                <AiIcon size={ICON_SIZES.SM} />
                <div className={`${BASE_CLASS}-blocks`}>
                  {message.blocks.map((block, blockIndex) => {
                    if (block.type === BLOCK_TYPES.AD_CARD) {
                      return (
                        <div key={blockIndex} className={`${BASE_CLASS}-blocks-ad-card`}>
                          <NetAdCard
                            ad={block}
                            onAdClick={onAdClick}
                            onFavoriteChange={onFavoriteChange}
                            setIsVisibleLoginModal={setIsVisibleLoginModal}
                            design={CARD_DESIGN.HORIZONTAL}
                            isWide={isWide}
                            extendedTrackingData={{listType: NET_ASSISTANT_AD_CARD, hit_information: chatId}}
                          />
                        </div>
                      )
                    }

                    if (block.type === BLOCK_TYPES.TEXT) {
                      const isLastMessage = index === messages.length - 1
                      const isLastBlock = blockIndex === message.blocks.length - 1
                      const shouldShowRetry = block.error && isLastMessage && isLastBlock

                      return (
                        <div key={blockIndex} className={`${BASE_CLASS}-blocks-message`}>
                          <MarkdownContent content={block.content} />
                          {shouldShowRetry && <NetRetryButton chatId={chatId} retrying={retrying} onRetry={onRetry} />}
                        </div>
                      )
                    }
                    if (block.type === BLOCK_TYPES.RESULTS) {
                      const totalResults = block.response?.meta?.totalResults
                      const items = block.response?.items || []
                      return (
                        <div key={blockIndex} className={`${BASE_CLASS}-searchResults`}>
                          <FilterTagsCard filters={block.filters} isWide={isWide}>
                            <SearchSave
                              type={searchSaveTypes.assistant}
                              isFullWidth={!isWide || (isWide && !isHigherThanMobileResolution)}
                              size={atomButtonSizes.LARGE}
                              design={atomButtonDesigns.FLAT}
                              trackingOrigin={searchSaveTrackingOrigins.NET_ASSISTANT}
                              authenticationHitInformation2={AUTHENTICATION_HIT_INFORMATIONS_2.NET}
                              search={block.filters}
                              showTooltip={false}
                              createAlertAfterLogin
                              onSuccess={() => onSearchSaved({messageId: message.messageId, blockIndex})}
                              setLoginModalVisible={setIsVisibleLoginModal}
                              disabled={block.searchSaved}
                              customText={
                                block.searchSaved
                                  ? i18n.t('NET.SEARCH_RESULTS.SAVED_SEARCH')
                                  : i18n.t('NET.SEARCH_RESULTS.SAVE_SEARCH')
                              }
                              icon={
                                block.searchSaved ? (
                                  <IconBellCheckOutline {...saveButtonIconProps} />
                                ) : (
                                  <IconBellRingOutline {...saveButtonIconProps} />
                                )
                              }
                              portalContainer={typeof document !== 'undefined' ? document.body : undefined}
                              extendedTrackingData={{hit_information: chatId}}
                            />
                          </FilterTagsCard>
                          {!!totalResults && (
                            <NetAdCarousel
                              items={items}
                              chatId={chatId}
                              isWide={isWide}
                              onAdClick={onAdClick}
                              onFavoriteChange={onFavoriteChange}
                              setIsVisibleLoginModal={setIsVisibleLoginModal}
                            />
                          )}
                          <div
                            className={cx(`${BASE_CLASS}-searchResultsFooter`, {
                              [`${BASE_CLASS}-searchResultsFooter--wide`]: isWide
                            })}
                          >
                            <MessageReview
                              onReviewSuccess={(messageId, type) =>
                                onReviewSuccess(messageId, type, userChatStep, block)
                              }
                              messageId={message.messageId}
                              messageReaction={message.reaction}
                            />
                            {!!totalResults && (
                              <AtomButton
                                className={`${BASE_CLASS}-resultsButton`}
                                onClick={() => onShowResults(block)}
                                shape={atomButtonShapes.CIRCULAR}
                                size={atomButtonSizes.LARGE}
                              >
                                {i18n.t('NET.SEARCH_RESULTS.SHOW_RESULTS', {count: i18n.n(totalResults)})}
                              </AtomButton>
                            )}
                          </div>
                        </div>
                      )
                    }

                    if (block.type === BLOCK_TYPES.CONTACT_OPTIONS) {
                      return (
                        <div
                          key={blockIndex}
                          className={cx(`${BASE_CLASS}-contactOptions`, {
                            [`${BASE_CLASS}-contactOptions--wide`]: isWide
                          })}
                        >
                          <ContactOptions
                            ad={block}
                            defaultMessage={block.text}
                            extendedLeadTrackingData={{hit_information: chatId, adId: block.adId}}
                            onClickContact={(ad, extendedTrackingData) =>
                              onClickContact({ad, message: block.text, extendedTrackingData})
                            }
                          />
                        </div>
                      )
                    }
                    return null
                  })}
                </div>
              </>
            )}
          </div>
        )
      })}
      {hasEndChat && <NetEndChatCard onNewChat={onNewChat} chatId={chatId} />}
    </div>
  )
}

NetMessageList.displayName = 'NetMessageList'

NetMessageList.propTypes = {
  chatId: PropTypes.string,
  isWide: PropTypes.bool,
  messages: PropTypes.arrayOf(
    PropTypes.shape({
      role: PropTypes.oneOf(Object.values(MESSAGE_ROLES)),
      text: PropTypes.string,
      blocks: PropTypes.arrayOf(
        PropTypes.shape({
          type: PropTypes.oneOf(Object.values(BLOCK_TYPES)),
          content: PropTypes.string,
          filters: PropTypes.object,
          response: PropTypes.shape({
            meta: PropTypes.shape({
              totalResults: PropTypes.number,
              page: PropTypes.number,
              size: PropTypes.number
            }),
            items: PropTypes.array
          })
        })
      ),
      loading: PropTypes.bool
    })
  ),
  onNewChat: PropTypes.func,
  onReviewSuccess: PropTypes.func,
  onShowResults: PropTypes.func,
  onAdClick: PropTypes.func,
  onSearchSaved: PropTypes.func,
  setIsVisibleLoginModal: PropTypes.func,
  onRetry: PropTypes.func,
  retrying: PropTypes.bool,
  restoring: PropTypes.bool,
  onFavoriteChange: PropTypes.func,
  onClickContact: PropTypes.func
}

export default NetMessageList
